import React, { useEffect, useState } from 'react';
import {
  Grid, TextField, Typography, Box, MenuItem, Autocomplete,
} from '@mui/material';
import { DatosRemitente, TipoDocumentoCO } from './EnvioNacionalModal.types';
import { getPersonaCOFieldError, buildDireccionCO, filtrarCiudadesCO } from './EnvioNacionalModal.utils';
import { DireccionColombiaForm, TipoVia, DireccionCOTouched } from './DireccionColombiaForm';
import { IndicativoPaisField } from './IndicativoPaisField';

export interface CiudadCO {
  id: string;
  label: string;
  codigoCm: string;
}

const TIPOS_DOCUMENTO: { value: TipoDocumentoCO; label: string }[] = [
  { value: 'CC', label: 'CC Cédula ciudadanía' },
  { value: 'CE', label: 'CE Cédula extranjería' },
  { value: 'PA', label: 'PA Pasaporte' },
  { value: 'NIT', label: 'NIT Número de identificación tributaria' },
];

const ALPHANUMERIC_ID = /[^a-zA-Z0-9ñÑ\s-]/g;

const compactFieldSx = {
  '& .MuiFormHelperText-root': { mt: 0.75, mx: 0, minHeight: '1.1em', lineHeight: 1.2 },
};

type TouchedPersona = Partial<Record<keyof DatosRemitente, boolean>>;

interface Props {
  datos: DatosRemitente;
  labels: { titulo: string; nombre: string };
  ciudades: CiudadCO[];
  tiposVia: TipoVia[];
  isLoadingCiudades?: boolean;
  onChange: (field: keyof DatosRemitente, value: string) => void;
  onCiudadSelect: (ciudad: CiudadCO) => void;
  onDireccionChange: (tipoVia: string, via: string, numeroVia: string, direccion: string) => void;
}

export const PersonaFormColombia: React.FC<Props> = ({
  datos, labels, ciudades, tiposVia,
  isLoadingCiudades = false,
  onChange, onCiudadSelect, onDireccionChange,
}) => {
  const [touched, setTouched] = useState<TouchedPersona>({});
  const [dirTouched, setDirTouched] = useState<DireccionCOTouched>({});
  const [ciudadQuery, setCiudadQuery] = useState(datos.ciudadLabel ?? '');

  const mark = (field: keyof DatosRemitente) =>
    setTouched(prev => ({ ...prev, [field]: true }));

  const markDir = (field: keyof DireccionCOTouched) =>
    setDirTouched(prev => ({ ...prev, [field]: true }));

  const fieldError = (field: keyof DatosRemitente): string =>
    touched[field] ? getPersonaCOFieldError(field, datos, tiposVia) : '';

  const selectedCity = ciudades.find(c => c.id === datos.ciudadId) ?? null;

  useEffect(() => {
    if (!ciudadQuery && selectedCity?.label) {
      setCiudadQuery(selectedCity.label);
    }
  }, [selectedCity, ciudadQuery]);

  const handleTipoVia = (tipoVia: string) => {
    const via = datos.via ?? '';
    const numeroVia = '';
    onDireccionChange(tipoVia, via, numeroVia, buildDireccionCO(tipoVia, via, numeroVia, tiposVia));
  };

  const handleVia = (via: string) => {
    const tipoVia = datos.tipoVia ?? '';
    const numeroVia = datos.numeroVia ?? '';
    onDireccionChange(tipoVia, via, numeroVia, buildDireccionCO(tipoVia, via, numeroVia, tiposVia));
  };

  const handleNumeroVia = (numeroVia: string) => {
    const tipoVia = datos.tipoVia ?? '';
    const via = datos.via ?? '';
    onDireccionChange(tipoVia, via, numeroVia, buildDireccionCO(tipoVia, via, numeroVia, tiposVia));
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary"
        sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {labels.titulo}
      </Typography>

      <Grid container spacing={1} alignItems="flex-start">
        {/* Nombre */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required size="small"
            sx={compactFieldSx}
            label={labels.nombre}
            value={datos.nombre}
            onChange={e => onChange('nombre', e.target.value.slice(0, 50))}
            onBlur={() => mark('nombre')}
            error={!!fieldError('nombre')}
            helperText={fieldError('nombre') || `${datos.nombre.length}/50`}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
        </Grid>

        {/* Tipo de documento */}
        <Grid size={{ xs: 6 }}>
          <TextField
            select fullWidth required size="small"
            sx={compactFieldSx}
            label="Tipo de documento"
            value={datos.tipoDocumento ?? 'CC'}
            onChange={e => onChange('tipoDocumento', e.target.value)}
            helperText=" "
          >
            {TIPOS_DOCUMENTO.map(t => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Identificación */}
        <Grid size={{ xs: 6 }}>
          <TextField
            fullWidth size="small"
            sx={compactFieldSx}
            label="Identificación"
            value={datos.identificacion ?? ''}
            onChange={e =>
              onChange('identificacion', e.target.value.replaceAll(ALPHANUMERIC_ID, '').slice(0, 16))
            }
            onBlur={() => mark('identificacion')}
            error={!!fieldError('identificacion')}
            helperText={fieldError('identificacion') || `${(datos.identificacion ?? '').length}/16`}
            slotProps={{ htmlInput: { maxLength: 16 } }}
          />
        </Grid>

        {/* Indicativo de país + Número celular */}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid size={{ xs: 4, sm: 3 }}>
              <IndicativoPaisField
                size="small"
                value={datos.indicativoPais}
                onChange={dialCode => onChange('indicativoPais', dialCode)}
                onBlur={() => mark('indicativoPais')}
                error={touched.indicativoPais && !datos.indicativoPais}
                helperText={touched.indicativoPais && !datos.indicativoPais ? 'Requerido' : ' '}
              />
            </Grid>
            <Grid size={{ xs: 8, sm: 9 }}>
              <TextField
                fullWidth required size="small"
                sx={compactFieldSx}
                label="Número celular"
                value={datos.numeroCelular}
                onChange={e =>
                  onChange('numeroCelular', e.target.value.replaceAll(/\D/g, '').slice(0, 12))
                }
                onBlur={() => mark('numeroCelular')}
                error={!!fieldError('numeroCelular')}
                helperText={fieldError('numeroCelular') || `${datos.numeroCelular.length}/12`}
                inputMode="numeric"
                slotProps={{ htmlInput: { maxLength: 12 } }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Ciudad (typeahead) */}
        <Grid size={{ xs: 12 }}>
          <Autocomplete
            options={ciudades}
            value={selectedCity}
            inputValue={ciudadQuery}
            loading={isLoadingCiudades}
            onInputChange={(_, value) => setCiudadQuery(value)}
            onChange={(_, selected) => {
              if (selected) {
                onCiudadSelect(selected);
                setCiudadQuery(selected.label);
              }
            }}
            getOptionLabel={c => (typeof c === 'string' ? c : c.label)}
            getOptionKey={c => (typeof c === 'string' ? c : c.id)}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            filterOptions={(opts, { inputValue }) => filtrarCiudadesCO(opts, inputValue)}
            onBlur={() => mark('ciudadId')}
            autoHighlight
            renderInput={params => (
              <TextField
                {...params} required size="small"
                sx={compactFieldSx}
                label="Ciudad"
                error={touched.ciudadId && !datos.ciudadId}
                helperText={touched.ciudadId && !datos.ciudadId ? 'Campo requerido' : undefined}
              />
            )}
            noOptionsText="Sin resultados"
            loadingText="Cargando ciudades..."
            slotProps={{ listbox: { sx: { maxHeight: 300 } } }}
          />
        </Grid>

        {/* Dirección estructurada */}
        <Grid size={{ xs: 12 }}>
          <DireccionColombiaForm
            tipoVia={datos.tipoVia ?? ''}
            via={datos.via ?? ''}
            numeroVia={datos.numeroVia ?? ''}
            detalleDireccion={datos.detalleDireccion}
            tiposVia={tiposVia}
            touched={dirTouched}
            onChangeTipoVia={handleTipoVia}
            onChangeVia={handleVia}
            onChangeNumeroVia={handleNumeroVia}
            onChangeDetalle={v => onChange('detalleDireccion', v)}
            onBlur={markDir}
          />
        </Grid>

        {/* Correo electrónico */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required size="small"
            sx={compactFieldSx}
            label="Correo electrónico"
            type="email"
            value={datos.correo}
            onChange={e => onChange('correo', e.target.value.trim())}
            onBlur={() => mark('correo')}
            error={!!fieldError('correo')}
            helperText={fieldError('correo') || undefined}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
